import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle
} from 'react'

import { useTranslation } from 'react-i18next'

import ErrorModal from '../error-modal/error-modal'

import {
  useEndpointStatusData,
  useEndpointStatusActions
} from '../../store/endpointStatus'
import { useGlobalStoreData } from '../../store/globalStore'
import { useSDSsearchParams } from '../../store/searchDataStore'

import McPageHelpers from '../../pages/mc-components/McPageHelpers'
import { HOMEPAGE_URL } from '../../services/api-common'

const showErrorModal = (
  fireErrorModal, errorMessage, t, searchParams, allowedCountries,
  languageGSD) => {
  switch (errorMessage?.ruleType) {
    case 'HS_CODE_COUNT':
      fireErrorModal(
        t('error_hs_code_count_limit_text', { limit: errorMessage.limit }),
        errorMessage.customMessage.split(',').map((obj, index) => <span key={index}>{McPageHelpers.findCountryName(
          obj)}<br/></span>)
      )
      break
    case 'HS_CODE_SET':
      fireErrorModal(
        t('error_hs_code_set_text', { hsCode: searchParams.hsCode }),
        errorMessage.customMessage.split(',').map((obj, index) => <span key={index}>{McPageHelpers.findCountryName(
          obj)}<br/></span>)
      )
      break
    case 'SEARCH_COUNTRY_SET':
      fireErrorModal(
        t('error_search_country_set_text', {
          exporter: McPageHelpers.findCountryName(searchParams.countryToExport)
        }),
        allowedCountries && allowedCountries.map(
          (obj, index) => <span key={index}>{McPageHelpers.findCountryName(
            obj)}<br/></span>)
      )
      break
    case 'MARKET_REPORT_TOTAL_SEARCH_COUNT':
      fireErrorModal(
        t('error_market_report_total_search_limit_title',
          { limit: errorMessage.limit / 10 }),
        t('error_market_report_total_search_limit_text',
          { currentValue: errorMessage.currentValue / 10 })
      )
      break
    case 'P7_FIRM_VIEW_COUNT':
      fireErrorModal(
        t('error_p7_view_limit_title',
          { limit: errorMessage.limit }),
        t('error_p7_view_limit_text',
          { currentValue: errorMessage.currentValue })
      )
      break
    case 'ALL_FIRM_VIEW_COUNT':
      fireErrorModal(
        t('error_all_view_limit_title',
          { limit: errorMessage.limit }),
        t('error_all_view_limit_text',
          { currentValue: errorMessage.currentValue })
      )
      break
    case 'TRADE_REGISTRY_FIRM_VIEW_COUNT':
      fireErrorModal(
        t('error_treg_total_view_limit_title',
          { limit: errorMessage.limit }),
        t('error_treg_total_view_limit_text',
          { currentValue: errorMessage.currentValue })
      )
      break
    case 'MARKET_REPORT_DAILY_SEARCH_COUNT':
      fireErrorModal(
        t('error_daily_search_limit_title',
          { limit: Math.round(errorMessage.limit / 10) }),
        t('error_daily_search_limit_text',
          { currentValue: Math.round(errorMessage.currentValue / 10) })
      )
      break
    case 'MARKET_REPORT_ANONYMOUS_DAILY_SEARCH_COUNT':
      fireErrorModal(
        t('error_anonymous_daily_search_limit_title',
          { limit: Math.round(errorMessage.limit) }),
        t('error_anonymous_daily_search_limit_text',
          { currentValue: Math.round(errorMessage.currentValue) }),
        `${HOMEPAGE_URL}/${languageGSD}/register/tradecalculus`
      )
      break
    case 'ALL_FIRM_DOWNLOAD_COUNT':
    case 'P7_FIRM_DOWNLOAD_COUNT':
    case 'MARKET_REPORT_EXCEL_DOWNLOAD_COUNT':
      fireErrorModal(
        t('error_excel_download_count_limit_title',
          { limit: errorMessage.limit }),
        t('error_excel_download_count_limit_text',
          { currentValue: errorMessage.currentValue })
      )
      break
    case 'TRADE_REGISTRY_FIRM_DOWNLOAD_COUNT':
      fireErrorModal(
        t('error_excel_download_count_limit_title',
          { limit: errorMessage.limit }),
        t('error_excel_download_count_limit_text',
          { currentValue: errorMessage.currentValue })
      )
      break
    case 'HS_CODE_AI_SEARCH_COUNT':
      fireErrorModal(
        t('error_hs_code_ai_search_count_limit_title',
          { limit: errorMessage.limit }),
        t('error_hs_code_ai_search_count_limit_text',
          { currentValue: errorMessage.currentValue })
      )
      break
    case 'PRODUCT_DETAIL_SEARCH_COUNT':
      fireErrorModal(
        t('error_product_detail_search_count_limit_title',
          { limit: errorMessage.limit / 10 }),
        t('error_product_detail_search_count_limit_text',
          { currentValue: errorMessage.currentValue / 10 })
      )
      break
    case 'MARKET_REPORT_EXCEL_FIRM_DOWNLOAD_COUNT':
      fireErrorModal(
        t('error_excel_firm_download_count_limit_title',
          { limit: errorMessage.limit }),
        t('error_excel_firm_download_count_limit_text',
          { currentValue: errorMessage.currentValue })
      )
      break
  }
}

const RuleErrorModalHandler = forwardRef((props, ref) => {
  const { t } = useTranslation()

  const userGSD = useGlobalStoreData.user()
  const languageGSD = useGlobalStoreData.language()

  const searchParams = useSDSsearchParams()

  const errorMessage = useEndpointStatusData.errorMessage()
  const { setEndpointStatus } = useEndpointStatusActions()

  const searchCountrySet = userGSD?.rules?.find(
    item => item.type === 'SEARCH_COUNTRY_SET')
  const allowedCountries = searchCountrySet?.parameters?.allowedSearchCountries

  const [modalFail, setModalFail] = useState(false)
  const [errorModalMessage, setErrorModalMessage] = useState()
  const [errorModalTitle, setErrorModalTitle] = useState()
  const [errorModalHref, setErrorModalHref] = useState(null)

  const fireErrorModal = (title, message, href = null) => {
    setErrorModalHref(href)
    setErrorModalTitle(title)
    setErrorModalMessage(message)
    setModalFail(true)
  }

  useImperativeHandle(ref, () => ({
    fireErrorModal
  }))

  useEffect(() => {
    showErrorModal(
      fireErrorModal,
      errorMessage,
      t,
      searchParams,
      allowedCountries,
      languageGSD
    )
  }, [errorMessage])

  return (
    <ErrorModal
      isOpen={modalFail}
      toggleModalFail={() => setModalFail((prevState) => !prevState)}
      title={errorModalTitle}
      message={errorModalMessage}
      linkText={errorModalHref
        ? t('error_anonymous_daily_search_linkText')
        : null}
      href={errorModalHref}
      clearErrorState={() => { setEndpointStatus('errorMessage') }}
    />
  )
})

export default RuleErrorModalHandler
